import React, { useState } from "react";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../components/panel/panel.jsx";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import "./../compnay/AddCompanyStyles.css";

function AddSmsPackage() {
  const navigate = useNavigate();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    isActive: true,
    vatPercent: 20,
    smsQuantity: 100,
    price: "",
    actualPrice: "",
    cardStyle: "Default",
    sortOrder: 0,
  });

  const inputStyle = {
    borderRadius: "10px",
    border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
    background: isDarkMode ? "#2d3436" : "#F9F9F9",
    color: isDarkMode ? "#ffffff" : "#333333",
    height: "44px",
  };

  const labelColor = { color: isDarkMode ? "#e0e0e0" : "#495057" };
  const mutedColor = { color: isDarkMode ? "#adb5bd" : "#6c757d" };

  const handleChange = (e, field) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [field]: value });
  };

  const handleSubmit = async () => {
    const vatPercent = parseFloat(form.vatPercent);
    const qty = parseInt(form.smsQuantity, 10);
    const sell = Math.round((parseFloat(form.price) || 0) * 100) / 100;
    const listRaw = parseFloat(form.actualPrice);
    const list =
      !Number.isNaN(listRaw) && listRaw > 0
        ? Math.round(listRaw * 100) / 100
        : sell;

    if (!form.name.trim()) {
      showErrorToast("Please enter a package name.");
      return;
    }
    if (!qty || qty <= 0) {
      showErrorToast("SMS quantity must be greater than 0.");
      return;
    }
    if (sell <= 0) {
      showErrorToast("Sell price must be greater than 0.");
      return;
    }
    if (list < sell) {
      showErrorToast("List / strike price cannot be less than sell price.");
      return;
    }
    if (Number.isNaN(vatPercent) || vatPercent < 0 || vatPercent > 100) {
      showErrorToast("VAT percent must be between 0 and 100.");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_ADD_SMS_PACKAGE, {
        Name: form.name.trim(),
        Description: form.description || "",
        SmsQuantity: qty,
        Price: sell,
        ActualPrice: list,
        VatPercent: vatPercent,
        Currency: "GBP",
        CardStyle: form.cardStyle || "Default",
        IsActive: form.isActive,
        SortOrder: parseInt(form.sortOrder, 10) || 0,
      });

      if (!response.data.status) {
        showErrorToast(response.data.message || "Failed to create SMS package");
        return;
      }

      showSuccessToast("SMS package created successfully");
      navigate("/smsPackages/manage");
    } catch (error) {
      console.error(error);
      showErrorToast("Failed to create SMS package");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-2 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/smsPackages/manage">SMS Packages</Link>
            </li>
            <li className="breadcrumb-item active">Add</li>
          </ol>
          <h1 className="page-header mb-0">Add SMS Package</h1>
        </div>
        <div className="ms-auto">
          <button
            onClick={handleSubmit}
            className="btn px-4"
            disabled={loading}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Create SMS Package
          </button>
        </div>
      </div>

      <Panel>
        <PanelHeader>SMS top-up details</PanelHeader>
        <PanelBody>
          <div className="row mb-3">
            <div className="col-12">
              <FormInput
                label="Package Name"
                placeHolder="e.g. Basic, Premium Offer"
                type="text"
                value={form.name}
                onchange={(e) => handleChange(e, "name")}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              <label style={labelColor}>Description</label>
              <textarea
                className="form-control"
                placeholder="e.g. For starters"
                value={form.description}
                onChange={(e) => handleChange(e, "description")}
                rows="2"
                style={{
                  borderRadius: "10px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                  background: isDarkMode ? "#2d3436" : "#F9F9F9",
                  color: isDarkMode ? "#ffffff" : "#333333",
                }}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-3">
              <label style={labelColor}>SMS quantity</label>
              <input
                type="number"
                className="form-control"
                min="1"
                value={form.smsQuantity}
                onChange={(e) => handleChange(e, "smsQuantity")}
                style={inputStyle}
              />
            </div>
            <div className="col-md-3">
              <label style={labelColor}>Sell price (£)</label>
              <input
                type="number"
                className="form-control"
                min="0.01"
                step="0.01"
                value={form.price}
                onChange={(e) => handleChange(e, "price")}
                style={inputStyle}
                placeholder="65.00"
              />
            </div>
            <div className="col-md-3">
              <label style={labelColor}>List / strike (£)</label>
              <input
                type="number"
                className="form-control"
                min="0"
                step="0.01"
                value={form.actualPrice}
                onChange={(e) => handleChange(e, "actualPrice")}
                style={inputStyle}
                placeholder="Optional e.g. 105"
              />
              <small style={mutedColor}>For offer strike-through</small>
            </div>
            <div className="col-md-3">
              <label style={labelColor}>VAT (%)</label>
              <input
                type="number"
                className="form-control"
                min="0"
                max="100"
                step="0.01"
                value={form.vatPercent}
                onChange={(e) => handleChange(e, "vatPercent")}
                style={inputStyle}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-4">
              <label style={labelColor}>Card style (EPOS)</label>
              <select
                className="form-control"
                value={form.cardStyle}
                onChange={(e) => handleChange(e, "cardStyle")}
                style={inputStyle}
              >
                <option value="Default">Default</option>
                <option value="Premium">Premium (blue)</option>
                <option value="Golden">Golden</option>
              </select>
            </div>
            <div className="col-md-4">
              <label style={labelColor}>Sort order</label>
              <input
                type="number"
                className="form-control"
                value={form.sortOrder}
                onChange={(e) => handleChange(e, "sortOrder")}
                style={inputStyle}
              />
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="smsActive"
                  checked={form.isActive}
                  onChange={(e) => handleChange(e, "isActive")}
                />
                <label className="form-check-label" htmlFor="smsActive" style={labelColor}>
                  Active on EPOS
                </label>
              </div>
            </div>
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}

export default AddSmsPackage;
